import { NavTag } from "@ugitgud/legos/ui/form/tag/NavTag";

export interface AppFeatureProps {
  title: string | React.ReactNode;
  description: string | React.ReactNode;
  imgSrc: string;
  tag?: "beta" | "new";
}

export default function AppFeature(props: AppFeatureProps) {
  const { title, description, imgSrc, tag } = props;

  return (
    <div className="app-feature">
      <div className="app-feature_preview">
        <img src={imgSrc} />
      </div>
      <div className="app-feature_description-section">
        <div className="header-container">
          {tag && (
            <NavTag shrink={false} size="sm" colorScheme="primary" className="mr-3 py-0.5 px-1 bg-accent-blue-400 rounded-sm">
              NEW
            </NavTag>
          )}
          <div className="caption-title">{title}</div>
        </div>
        <div className="caption-description">{description}</div>
      </div>
    </div>
  );
}

export const featureFootnote = "";

export const LolFeatureCards: AppFeatureProps[] = [
  {
    title: "Team Gold Difference",
    description: "In-game realized gold difference for lane matchups",
    imgSrc: "https://static.bigbrain.gg/assets/desktop-app/onboarding/ftue/gold-diff.png",
    tag: "new",
  },
  {
    title: "Video Highlights",
    description: "Re-live your best plays with auto-generated match highlights",
    imgSrc: "https://static.bigbrain.gg/assets/desktop-app/onboarding/ftue/video-highlights.png",
    tag: "new",
  },
  {
    title: "Live Stat Tracker",
    description: "Track key in-game metrics by elo, champion, and role",
    imgSrc: "https://static.bigbrain.gg/assets/desktop-app/onboarding/ftue/live-stat-tracker.png",
  },
  {
    title: "Skill Leveling Order",
    description: "Get in-game dynamic skill order recommendations for all situations",
    imgSrc: "https://static.bigbrain.gg/assets/desktop-app/onboarding/ftue/skill-leveling-order.png",
  },
  {
    title: "In-Game Builds",
    description: "Itemize optimally every time with in-game builds using the best data",
    imgSrc: "https://static.bigbrain.gg/assets/desktop-app/onboarding/ftue/in-game-build.png",
  },
  {
    title: "Champion Select Scouting",
    description: "Scout everything, from the best picks & counters to team synergies",
    imgSrc: "https://static.bigbrain.gg/assets/desktop-app/onboarding/ftue/champion-select-scouting.png",
  },
  {
    title: "Auto-Import Loadouts",
    description: "Auto-import the best builds, tailored by elo, role, and matchup",
    imgSrc: "https://static.bigbrain.gg/assets/desktop-app/onboarding/ftue/auto-import.png",
  },
  {
    title: "Profiles with LP Tracking",
    description: "Track your progress on the first app to have LP per game",
    imgSrc: "https://static.bigbrain.gg/assets/desktop-app/onboarding/ftue/profiles.png",
  },
  {
    title: "Live Game",
    description: "Get an edge with additional in-game scouting on all players",
    imgSrc: "https://static.bigbrain.gg/assets/desktop-app/onboarding/ftue/live-game.png",
  },
  {
    title: "Post-Game Analytics",
    description: "See how you really did, from timeline data to carry score",
    imgSrc: "https://static.bigbrain.gg/assets/desktop-app/onboarding/ftue/post-game.png",
  },
  {
    title: "Leaderboards",
    description: "Keep up with ranked leaderboards for all regions and elos",
    imgSrc: "https://static.bigbrain.gg/assets/desktop-app/onboarding/ftue/leaderboards.png",
  },
];

export const valFeatureCards: AppFeatureProps[] = [
  {
    title: "In-Game Util Tracking",
    description: "Break down the chaos after each round with in-game utility tracking for every fight.",
    imgSrc: "https://static.bigbrain.gg/val/img/ftue/util-tracking.png",
    tag: "beta",
  },
  {
    title: "Peek Maps",
    description: "Discover the best spots on each map by KDA, weapon, agent role, and side. Updated daily.",
    imgSrc: "https://static.bigbrain.gg/val/img/ftue/peekmap.png",
  },
  {
    title: "Live Player Scouting",
    description: "Scout everything with up-to-date player stats in Agent Select and Loading Screen.",
    imgSrc: "https://static.bigbrain.gg/val/img/ftue/agent-select.png",
  },
  {
    title: "In-Game Stats",
    description: "Get live performance averages and last round's metrics, including custom metrics like Time To Kill",
    imgSrc: "https://static.bigbrain.gg/val/img/ftue/in-game-stats.png",
  },
  {
    title: "In-Game Round Highlights",
    description: "Re-live your best plays from the last round with in-game highlights for kills and utility usage",
    imgSrc: "https://static.bigbrain.gg/val/img/ftue/post-round.png",
  },
  {
    title: "Player Profiles",
    description: "Track your climb and personal performance by map, agent, and gun",
    imgSrc: "https://static.bigbrain.gg/val/img/ftue/profiles.png",
  },
  {
    title: "Leaderboards",
    description: "Keep up with the ranked leaderboards for the best players in every region",
    imgSrc: "https://static.bigbrain.gg/val/img/ftue/leaderboards.png",
  },
  {
    title: "Tier Lists",
    description: "Learn the best agents and weapons for each map, among other data-driven insights",
    imgSrc: "https://static.bigbrain.gg/val/img/ftue/tierlists.png",
  },
];

export const deadlockFeatureCards: AppFeatureProps[] = [
  {
    title: "Mid Boss & Buff Timers",
    description: "Never Miss that Gun Buff again with our in-game timers.",
    imgSrc: "https://static.bigbrain.gg/deadlock/image-assets/ftue/deadlock-mid-boss.png",
  },
  {
    title: "Always Updated Profiles",
    description: "100% Accurate Match Histories without any missing games.",
    imgSrc: "https://static.bigbrain.gg/deadlock/image-assets/ftue/deadlock-profiles.png",
  },
  {
    title: "Rank Distribution",
    description: "Track the ever changing player base rank disparity.",
    imgSrc: "https://static.bigbrain.gg/deadlock/image-assets/ftue/deadlock-rank.png",
  },
  {
    title: "Player & Hero Leaderboards",
    description: "See the top ranked players overall and for each hero.",
    imgSrc: "https://static.bigbrain.gg/deadlock/image-assets/ftue/deadlock-leaderboards.png",
  },
];
